import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * What a Rider sees while the store decides.
 *
 * WHAT THIS REPLACED, AND WHY
 *
 * There was a "Check release status" button here. The trip already refreshes
 * every few seconds on its own, so the button re-asked a question that was
 * being asked continuously - and it asked it QUIETLY, deliberately suppressing
 * the loading state, so pressing it produced no spinner, no flicker, nothing.
 * It was indistinguishable from a dead control, and a Rider pressing it learned
 * only that the app seemed broken.
 *
 * The honest version is this: say that it is checking by itself, show that the
 * wait is being counted, and say whose move it is. A Rider standing in a shop
 * needs to know they are not being ignored and that there is nothing they have
 * forgotten to press.
 *
 * The elapsed time is not a deadline and is deliberately not styled as one. It
 * is there so a Rider can tell four minutes from forty, which is the difference
 * between waiting and going to ask someone.
 */

function elapsedLabel(since, now) {
  const started = since ? new Date(since).getTime() : NaN;
  if (!Number.isFinite(started)) return null;
  const seconds = Math.max(0, Math.round((now - started) / 1000));
  if (seconds < 60) return 'Waiting less than a minute';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `Waiting ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  const hours = Math.floor(minutes / 60);
  return `Waiting ${hours} ${hours === 1 ? 'hour' : 'hours'} ${minutes % 60} min`;
}

export default function WaitingForRelease({ since, parcelCount }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    // Once a minute is enough for a label that counts in minutes, and cheap
    // enough to leave running on a phone that is already tracking a trip.
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  const waited = elapsedLabel(since, now);

  return (
    <div role="status" className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 p-4 dark:border-sky-500/20 dark:bg-sky-500/10">
      <div className="flex items-start gap-3">
        <Loader2 className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-sky-700 motion-reduce:animate-none dark:text-sky-300" aria-hidden="true" />
        <div className="min-w-0">
          <p className="font-semibold text-sky-950 dark:text-sky-100">Checking for the store&rsquo;s release</p>
          <p className="mt-1 text-sm leading-6 text-sky-900 dark:text-sky-200">
            This updates by itself &mdash; there is nothing for you to press. Staff are checking
            {' '}{parcelCount === 1 ? 'the parcel' : `all ${parcelCount} parcels`} against their manifest, and this
            step moves on the moment they release.
          </p>
          {waited && <p className="mt-2 text-sm font-medium text-sky-900 dark:text-sky-200">{waited}</p>}
        </div>
      </div>
    </div>
  );
}
